import { useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { todayInWorkspaceTz } from "@/hooks/useWorkspaceTasks";
import type { CreateTaskInput } from "@/hooks/useWorkspaceTasks";

interface TaskQuickAddProps {
  memberId: string;
  onCreate: (input: CreateTaskInput) => Promise<void>;
}

export function TaskQuickAdd({ memberId, onCreate }: TaskQuickAddProps) {
  const [title, setTitle] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async () => {
    if (!title.trim()) return;
    try {
      setSubmitting(true);
      await onCreate({
        title: title.trim(),
        assigned_member_id: memberId,
        due_date: todayInWorkspaceTz(),
      });
      setTitle("");
      toast({ title: "Tarefa criada" });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      toast({ title: "Erro ao criar tarefa", description: message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="p-3 flex items-center gap-2">
      <Input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Adicionar tarefa rápida (prazo hoje)..."
        className="flex-1 h-9"
        onKeyDown={(e) => {
          if (e.key === "Enter") void handleSubmit();
        }}
        disabled={submitting}
      />
      <Button
        size="sm"
        onClick={() => void handleSubmit()}
        disabled={!title.trim() || submitting}
        className="shrink-0"
      >
        {submitting ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <>
            <Plus className="h-4 w-4 sm:mr-1" />
            <span className="hidden sm:inline">Adicionar</span>
          </>
        )}
      </Button>
    </Card>
  );
}
